// web-commands.ts — the PER-APP half of the web shape: maps oriora_statements' Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD (accounts + transactions) is a few lines each. The DERIVED commands are the app's
// on-device compute (parse.rs statement parsing + rollups.rs aggregates) — on web they return a
// valid-but-EMPTY shell matching the store.ts return type, so the UI never crashes. A faithful web
// build reimplements the parsing + rollup logic in JS (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, WebStore } from "./web-runtime";
import { adviceRefusal } from "./fence";
import { computeOverviewWeb, resolveWindowWeb, sliceForWindowWeb, type WebTxn } from "./rollups-web";
import { browserSearch, browserFetch } from "./web-search-browser";
import { parseCsv, categorize, dedupeKey } from "./parse-web";

const ACCOUNTS = "accounts";
const TRANSACTIONS = "transactions";

// Pre-LLM advice fence — 1:1 mirror of src-tauri/src/ipc.rs `advice_reason`, so the WEB shape
// refuses investment/budgeting/tax advice for free BEFORE any billed call, exactly like native.
// Keep this list identical to the Rust one.
// The advice fence now lives in ./fence.ts so a battery can exercise it without Tauri.
// (It was duplicated here AND in ipc.rs — two copies of a safety list is how one drifts.)

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Accounts ──
    // Native ORDER BY created_at ASC.
    list_accounts: () =>
      db
        .all(ACCOUNTS)
        .sort((a, b) =>
          String((a as { created_at?: string }).created_at ?? "").localeCompare(
            String((b as { created_at?: string }).created_at ?? ""),
          ),
        ),
    rename_account: ({ id, name }) => {
      db.update(ACCOUNTS, String(id), { name: String(name ?? "").trim() });
      return null;
    },
    delete_account: ({ id }) => {
      const accId = String(id);
      // Mirror ON DELETE CASCADE: drop the account's transactions too.
      for (const t of db.all(TRANSACTIONS)) {
        if ((t as { account_id?: string }).account_id === accId) db.remove(TRANSACTIONS, t.id);
      }
      db.remove(ACCOUNTS, accId);
      return null;
    },

    // ── Transactions ──
    // Native: filter by from/to/account, ORDER BY posted_on DESC, imported_at DESC, LIMIT.
    list_txns: ({ from, to, accountId, limit }) => {
      let rows = db.all(TRANSACTIONS).filter((r) => {
        const posted = String((r as { posted_on?: string }).posted_on ?? "");
        if (from && posted < String(from)) return false;
        if (to && posted > String(to)) return false;
        if (accountId && (r as { account_id?: string }).account_id !== String(accountId)) return false;
        return true;
      });
      rows.sort((a, b) => {
        const pa = String((a as { posted_on?: string }).posted_on ?? "");
        const pb = String((b as { posted_on?: string }).posted_on ?? "");
        if (pa !== pb) return pb.localeCompare(pa);
        const ia = String((a as { imported_at?: string }).imported_at ?? "");
        const ib = String((b as { imported_at?: string }).imported_at ?? "");
        return ib.localeCompare(ia);
      });
      const lim = typeof limit === "number" ? limit : 2000;
      return rows.slice(0, lim);
    },
    count_txns: () => db.all(TRANSACTIONS).length,
    set_category: ({ id, category }) => {
      db.update(TRANSACTIONS, String(id), { category: String(category ?? "") });
      return null;
    },
    delete_txn: ({ id }) => {
      db.remove(TRANSACTIONS, String(id));
      return null;
    },

    // ── Import (DERIVED — parse.rs ported to ./parse-web.ts; CSV only on web) ──
    // The web sheet reads the picked file's TEXT and passes it as `content` (there is no
    // filesystem path in a browser). PDF stays native-only — the sheet says so on web.
    parse_statement: ({ content, defaultCurrency }) => {
      const text = String(content ?? "");
      if (!text) throw new Error("pick the file again — the browser build parses file content, not a path");
      const rows = parseCsv(text, String(defaultCurrency ?? "GBP"));
      return {
        format: "csv",
        parsed: rows.length,
        currency: rows[0]?.currency ?? String(defaultCurrency ?? "GBP"),
        sample: rows.slice(0, 8),
      };
    },
    // Mirrors db.rs::upsert_account + import_txns: find-or-create the account by name
    // (case-insensitive), then insert with categorize + the same dedupe key, counting duplicates.
    import_statement: ({ content, accountName, accountKind, defaultCurrency }) => {
      const text = String(content ?? "");
      if (!text) throw new Error("pick the file again — the browser build parses file content, not a path");
      const rows = parseCsv(text, String(defaultCurrency ?? "GBP"));

      const trimmed = String(accountName ?? "").trim() || "Imported account";
      const kind = String(accountKind ?? "bank");
      let account = db.all(ACCOUNTS).find(
        (a) => String((a as { name?: string }).name ?? "").toLowerCase() === trimmed.toLowerCase(),
      ) as { id: string; name?: string } | undefined;
      if (!account) {
        account = db.insert(ACCOUNTS, { id: uuid(), name: trimmed, kind, created_at: nowIso() }) as { id: string; name?: string };
      }

      const seen = new Set(
        db.all(TRANSACTIONS).map((t) => String((t as { dedupe_key?: string }).dedupe_key ?? "")),
      );
      const now = nowIso();
      let inserted = 0;
      let duplicates = 0;
      for (const r of rows) {
        const key = dedupeKey(account.id, r);
        if (seen.has(key)) { duplicates++; continue; }
        seen.add(key);
        db.insert(TRANSACTIONS, {
          id: uuid(),
          account_id: account.id,
          posted_on: r.posted_on,
          merchant: r.merchant.trim(),
          raw_description: r.raw_description.trim(),
          amount: r.amount,
          currency: r.currency,
          category: categorize(r.merchant, r.raw_description),
          imported_at: now,
          dedupe_key: key,
        });
        inserted++;
      }
      return { inserted, duplicates, account_id: account.id, account_name: trimmed };
    },

    // ── Rollups (DERIVED — rollups.rs aggregates over the ledger) ──
    // Mirrors rollups.rs::overview (see ./rollups-web.ts). This was a ZERO STUB — every figure
    // came back 0 and every list empty, so the web surface showed nothing at all.
    compute_overview: ({ today }) =>
      computeOverviewWeb(db.all(TRANSACTIONS) as unknown as WebTxn[], String(today ?? new Date().toISOString().slice(0, 10))),
    // DERIVED — pre-LLM advice fence, 1:1 with the Rust keyword screen. null = "not advice, allowed".
    advice_check: ({ question }) => adviceRefusal(String(question ?? "")),
    // DERIVED — builds the scoped slice sent on the ONE AI call (mirror of rollups.rs).
    // Mirrors ipc.rs::build_query_slice: advice fence → resolve the window → the full slice.
    // (Was the last zero stub — every Ask on web answered "No transactions in ." with an empty label.)
    build_query_slice: ({ question, from, to, today }) => {
      const q = String(question ?? "");
      const refusal = adviceRefusal(q);
      if (refusal) throw new Error("advice_blocked");
      const todayDate = (() => {
        const t = String(today ?? "").slice(0, 10);
        return /^\d{4}-\d{2}-\d{2}$/.test(t) ? new Date(`${t}T00:00:00Z`) : new Date();
      })();
      const [f, t, label] = resolveWindowWeb(q, from ? String(from) : undefined, to ? String(to) : undefined, todayDate);
      return sliceForWindowWeb(db.all(TRANSACTIONS) as unknown as WebTxn[], q, f, t, label);
    },
  };
}
